import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { graphqlRequest } from '../../utils/graphqlRequest';

const GET_ADMIN_ROLE_PROFILES = `
  query GetAdminRoleProfiles {
    getAdminRoleProfiles {
      role
      users
      description
    }
  }
`;

const GET_ALL_USERS = `
  query GetAllUsersForRoleProfiles($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        email
        contact
        jobTitle
        roles
        isActive
      }
      total
      page
      totalPages
    }
  }
`;

const normalizeRoleLabel = (role) => {
  const cleaned = String(role || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return 'Unknown';
  return cleaned
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const roleKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

export default function RoleProfiles() {
  const navigate = useNavigate();
  const [rowsData, setRowsData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState('all');
  const [memberSearch, setMemberSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        Cookies.get('accessToken');

        const loadAllUsers = async () => {
          const all = [];
          let page = 1;
          let totalPages = 1;
          // Backend caps limit at 100 per page.
          do {
            const data = await graphqlRequest(GET_ALL_USERS, {
              page,
              limit: 100,
            });
            const pagePayload = data?.getAllUser;
            const chunk = Array.isArray(pagePayload?.data)
              ? pagePayload.data
              : [];
            all.push(...chunk);
            totalPages = Math.max(1, Number(pagePayload?.totalPages) || 1);
            page += 1;
          } while (page <= totalPages && page <= 20);
          return all;
        };

        const [rolesRes, usersRes] = await Promise.allSettled([
          graphqlRequest(GET_ADMIN_ROLE_PROFILES),
          loadAllUsers(),
        ]);

        if (rolesRes.status === 'fulfilled') {
          setRowsData(
            Array.isArray(rolesRes.value?.getAdminRoleProfiles)
              ? rolesRes.value.getAdminRoleProfiles
              : [],
          );
        } else {
          console.error('Failed to load role profiles:', rolesRes.reason);
          setRowsData([]);
        }

        if (usersRes.status === 'fulfilled') {
          setUsers(Array.isArray(usersRes.value) ? usersRes.value : []);
        } else {
          console.error('Failed to load users for role profiles:', usersRes.reason);
          setUsers([]);
        }
      } catch (error) {
        console.error('Failed to load role profiles:', error);
        setRowsData([]);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const roleCountsByKey = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    const counts = new Map();
    for (const u of list) {
      const roles = Array.isArray(u?.roles) ? u.roles : [];
      for (const r of roles) {
        const k = roleKey(r);
        if (!k) continue;
        counts.set(k, (counts.get(k) || 0) + 1);
      }
    }
    return counts;
  }, [users]);

  const roleOptions = useMemo(() => {
    const fromApi = rowsData
      .map((r) => ({
        label: String(r?.role || 'Unknown').trim() || 'Unknown',
        key: roleKey(r?.role),
        count: roleCountsByKey.get(roleKey(r?.role)) || 0,
        description: String(r?.description || '').trim(),
      }))
      .filter((r) => r.key);

    // Fallback: derive roles from the user list when the profiles query is empty/failed.
    const source =
      fromApi.length > 0
        ? fromApi
        : Array.from(roleCountsByKey.entries()).map(([key, count]) => {
            const sample = (users || []).find((u) =>
              (Array.isArray(u?.roles) ? u.roles : [])
                .map(roleKey)
                .includes(key),
            );
            const raw =
              (Array.isArray(sample?.roles) ? sample.roles : []).find(
                (r) => roleKey(r) === key,
              ) || key;
            return {
              label: normalizeRoleLabel(raw),
              key,
              count,
              description: '',
            };
          });

    const seen = new Set();
    const unique = [];
    for (const r of source) {
      if (seen.has(r.key)) continue;
      seen.add(r.key);
      unique.push(r);
    }
    unique.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    return unique;
  }, [rowsData, roleCountsByKey, users]);

  const members = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    return users
      .filter((u) => {
        const selectedKey = roleKey(selectedRole);
        if (selectedKey && selectedKey !== 'all') {
          const roleList = Array.isArray(u?.roles) ? u.roles : [];
          const keys = roleList.map(roleKey).filter(Boolean);
          if (!keys.includes(selectedKey)) return false;
        }
        if (!q) return true;
        const hay = `${u?.firstName || ''} ${u?.lastName || ''} ${u?.email || ''} ${u?.jobTitle || ''} ${(Array.isArray(u?.roles) ? u.roles.map(normalizeRoleLabel).join(' ') : '')}`
          .toLowerCase()
          .trim();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const an = `${a?.firstName || ''} ${a?.lastName || ''}`.trim();
        const bn = `${b?.firstName || ''} ${b?.lastName || ''}`.trim();
        return an.localeCompare(bn);
      });
  }, [memberSearch, selectedRole, users]);

  return (
    <div
      className="role-profiles-page"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(200px, 260px) minmax(0, 1fr)',
        gap: 14,
        alignItems: 'start',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        paddingBottom: 16,
      }}
    >
      <aside
        style={{
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          padding: 16,
          position: 'sticky',
          top: 12,
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 900, color: '#111827' }}>
          Role profiles
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
          Select a role to filter members. Use “All members” to see everyone.
        </div>

        <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
          <button
            type="button"
            onClick={() => setSelectedRole('all')}
            style={{
              textAlign: 'left',
              padding: '10px 12px',
              borderRadius: 10,
              border: selectedRole === 'all' ? '1px solid #4f46e5' : '1px solid #e5e7eb',
              background: selectedRole === 'all' ? 'rgba(79,70,229,0.08)' : '#fff',
              cursor: 'pointer',
              fontWeight: 800,
              color: '#111827',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            All members
          </button>

          {loading ? (
            <div style={{ padding: 10, color: '#6b7280', fontSize: 13 }}>
              Loading roles…
            </div>
          ) : roleOptions.length === 0 ? (
            <div style={{ padding: 10, color: '#6b7280', fontSize: 13 }}>
              No roles found.
            </div>
          ) : (
            roleOptions.map((r) => {
              const active = roleKey(selectedRole) === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setSelectedRole(r.label)}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: active ? '1px solid #4f46e5' : '1px solid #e5e7eb',
                    background: active ? 'rgba(79,70,229,0.08)' : '#fff',
                    cursor: 'pointer',
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      alignItems: 'baseline',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 900,
                        color: '#111827',
                        fontSize: 14,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {r.label}
                    </div>
                    <div
                      style={{
                        fontWeight: 900,
                        color: '#4f46e5',
                        fontSize: 12,
                        border: '1px solid rgba(79,70,229,0.25)',
                        padding: '2px 8px',
                        borderRadius: 999,
                        flexShrink: 0,
                      }}
                    >
                      {r.count}
                    </div>
                  </div>
                  {r.description ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                      {r.description}
                    </div>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </aside>

      <section
        style={{
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          padding: 16,
          minWidth: 0,
          maxWidth: '100%',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 200px' }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#111827' }}>
              Members
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
              Click “View profile” to open full profile (Overview, Timesheet, Payroll, Notes).
            </div>
          </div>

          <input
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            placeholder="Search name, email, title, role…"
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid #e5e7eb',
              flex: '1 1 220px',
              minWidth: 0,
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div
          style={{
            marginTop: 14,
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <table
            style={{
              width: '100%',
              minWidth: 720,
              borderCollapse: 'separate',
              borderSpacing: 0,
            }}
          >
            <thead>
              <tr>
                {['Name', 'Email', 'Job title', 'Phone', 'Roles', 'Status', ''].map((h) => (
                  <th
                    key={h || 'actions'}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderBottom: '1px solid #e5e7eb',
                      color: '#374151',
                      fontWeight: 800,
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                      background: '#fff',
                      ...(h === ''
                        ? {
                            position: 'sticky',
                            right: 0,
                            zIndex: 2,
                            boxShadow: '-6px 0 8px -6px rgba(0,0,0,0.12)',
                          }
                        : {}),
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: 14, color: '#6b7280' }}>
                    Loading members…
                  </td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 14, color: '#6b7280' }}>
                    No members found.
                  </td>
                </tr>
              ) : (
                members.map((u) => {
                  const fullName =
                    `${u?.firstName || ''} ${u?.lastName || ''}`.trim() ||
                    u?.email ||
                    '—';
                  const roleList = Array.isArray(u?.roles) ? u.roles : [];
                  return (
                    <tr key={u?.id}>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                        <strong style={{ color: '#111827' }}>{fullName}</strong>
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                        {u?.email || '—'}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                        {u?.jobTitle || '—'}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                        {u?.contact || '—'}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {roleList.length ? (
                            roleList.map((r) => (
                              <span
                                key={`${u?.id}-${String(r)}`}
                                style={{
                                  fontSize: 12,
                                  fontWeight: 800,
                                  padding: '3px 8px',
                                  borderRadius: 999,
                                  border: '1px solid #e5e7eb',
                                  background: '#f9fafb',
                                  color: '#111827',
                                }}
                              >
                                {normalizeRoleLabel(r)}
                              </span>
                            ))
                          ) : (
                            <span style={{ color: '#6b7280' }}>—</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 900,
                            padding: '3px 8px',
                            borderRadius: 999,
                            background: u?.isActive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.10)',
                            color: u?.isActive ? '#047857' : '#991b1b',
                          }}
                        >
                          {u?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '10px 12px',
                          borderBottom: '1px solid #f3f4f6',
                          position: 'sticky',
                          right: 0,
                          background: '#fff',
                          zIndex: 1,
                          boxShadow: '-6px 0 8px -6px rgba(0,0,0,0.12)',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => navigate(`/profile-dashboard/${u?.id}`)}
                          style={{
                            padding: '7px 10px',
                            background: '#111827',
                            color: '#fff',
                            border: 0,
                            borderRadius: 10,
                            cursor: 'pointer',
                            fontWeight: 900,
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          View profile
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

